// © Microsoft Corporation. All rights reserved.

import { IMockManager, MockConfigDictionary } from "@msnews/core-contracts";

import { MockExperienceConfig } from "@msnews/config-data-types";
import { get } from "lodash-es";
import { getLocationSearch } from "@msnews/isomorphic-util";

export class MockManager implements IMockManager {
    /** Indicates if mock is requested */
    private _mocksEnabled = false;

    /** Indicates if the config for mock manager has been initialized */
    private _shouldInitializeMockMgrConfig = false;

    /** Stores mock config replacements in kv pair */
    private _mockConfigDictionary: MockConfigDictionary = {};

    /**
     * Initialize mock manager
     */
    constructor() {
        // check QSP to see if mocks=true OR if it has mock parameters
        this._mocksEnabled = this.areMocksEnabled();
        if (this._shouldInitializeMockMgrConfig) {
            // fake entry to force MockManager's own config load to go through the mock config folder
            this._mockConfigDictionary["MockManager/default"] = "MockManager/default";
        }
    }

    /**
     * Returns the list of mocked config list
     */
    public get mockConfigDictionary(): MockConfigDictionary {
        return this._mockConfigDictionary;
    }

    /**
     * Indicates if mock configs was requested (by QSP)
     */
    public get mocksEnabled(): boolean {
        return this._mocksEnabled;
    }

    /**
     * Initialize mock manager's configs
     * @param mockConfig
     */
    public initialize(mockConfig: MockExperienceConfig): void {
        if (!mockConfig) {
            // No mock config, but check for mock QSP
            this.addMockQspToDictionary();
            return;
        }

        const mockScenariosFromQsp = [];

        // Mock params can be in one of two forms and should work for both
        // mock=scenario1,scenario2
        // mock=scenario1&mock=scenario2
        const mockParams = new URLSearchParams(getLocationSearch()).getAll("mock");
        mockParams.forEach(param => {
            const mockScenarios = param.trim().split(",");
            mockScenarios.forEach(entry => {
                if (mockScenariosFromQsp.indexOf(entry) < 0) {
                    mockScenariosFromQsp.push(entry);
                }
            });
        });

        if (mockScenariosFromQsp.length === 0) {
            return;
        }

        // Get scenarios defined in mock config
        const scenarios = mockConfig && mockConfig.scenarios;
        for (const key in scenarios) {
            // Check if key (scenario in this case) is defined in the object itself, not in parent
            if (scenarios[key] && mockScenariosFromQsp.indexOf(key) > -1) {
                const mockEntries = scenarios[key];

                mockEntries.forEach(entry => {
                    const { experienceType: srcExpType, instanceSrc: srcInstSrc } = entry.source.configLink;
                    const { experienceType: tgtExpType, instanceSrc: tgtInstSrc } = entry.target.configRef;

                    this._mockConfigDictionary[`${srcExpType}/${srcInstSrc}`] = `${tgtExpType}/${tgtInstSrc}`;
                });
            }
        }
        // Override scenario entries with those in mock qsp
        this.addMockQspToDictionary();
    }

    /**
     * Checks QSP to see if mock configs are requested either by
     * 1. Setting 'mockConfigs` to true AND mock scenarios (`mock=scenario1, scenanio2`) are present
     * 2. item=mock is given and that results in dictionary being initialized
     */
    private areMocksEnabled(): boolean {
        const search = new URLSearchParams(getLocationSearch());
        this._shouldInitializeMockMgrConfig = !!search.get("mock");
        return this._shouldInitializeMockMgrConfig || search.getAll("item").some(item => /^mock:/.test(item));
    }

    /**
     * Gets the list of mocks from QSP
     * eg: item=mock:river/default|river/mockinst returns what to mock (river/default) with replacement sought (river/mockinst)
     * This is used only in local dev/TEST scenario. Please do not use this for PROD scenarios
     */
    private addMockQspToDictionary(): void {
        // Get all parameters that begin with 'mock:'
        const itemParams = new URLSearchParams(getLocationSearch()).getAll("item").filter(item => /^mock:/.test(item));

        itemParams.forEach((param) => {
            const [_, mockParam] = param.split(":");
            const [mockFrom, mockWith] = mockParam.split("|");
            this._mockConfigDictionary[mockFrom] = mockWith;
        });
    }
}
export const mockManager = new MockManager();